// What a quote costs, by kind: one-time work, recurring services (a Workstream
// retainer, billed monthly for a term) and software (the Foundation license).
// Recurring services are services, not a license: they total and invoice on
// their own. Pure functions over the builder payload.
import type { BuilderLine, QuoteBuilder } from './quotesApi';

export interface RecurringLine {
  id: string;
  name: string;
  monthly: number;
  /** Months in its term (its service dates or revenue schedule); null when open-ended. */
  months: number | null;
  start: string | null;
  end: string | null;
  /** monthly x months; null when open-ended. */
  total: number | null;
}

export interface InvestmentSummary {
  oneTime: number;
  recurring: { lines: RecurringLine[]; monthly: number; total: number | null };
  /** What the customer pays monthly for software once this quote is accepted. */
  softwareMonthly: number;
}

/** Calendar months from `start` to `end` inclusive (2026-11-01 to 2027-01-31 is 3). */
export function monthsBetween(start: string, end: string): number {
  const [sy, sm] = start.split('-').map(Number) as [number, number];
  const [ey, em] = end.split('-').map(Number) as [number, number];
  return (ey - sy) * 12 + (em - sm) + 1;
}

const isRecurringService = (l: BuilderLine) => l.billing === 'monthly' && !l.parent_line_id && l.family !== 'software';

export function investmentSummary(data: QuoteBuilder): InvestmentSummary {
  const rows = data.schedules ?? [];
  const lines: RecurringLine[] = data.lines.filter(isRecurringService).map((l) => {
    const scheduled = rows.filter((r) => r.quote_line_id === l.id).length;
    const months = l.start_date && l.end_date ? monthsBetween(l.start_date, l.end_date) : scheduled || (l.term_months ?? null);
    return { id: l.id, name: l.name, monthly: l.net, months, start: l.start_date, end: l.end_date, total: months === null ? null : l.net * months };
  });
  const monthly = lines.reduce((sum, l) => sum + l.monthly, 0);
  const total = lines.length && lines.every((l) => l.total !== null) ? lines.reduce((sum, l) => sum + (l.total ?? 0), 0) : null;
  return {
    oneTime: data.totals.oneTime.net,
    recurring: { lines, monthly, total },
    softwareMonthly: Math.max(0, data.totals.monthly.after - monthly),
  };
}

/** YYYY-MM-DD plus `days`. */
export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export interface Invoice {
  date: string;
  /** The month it pays for, YYYY-MM. */
  month: string;
  amount: number;
  due: string;
}

/**
 * The recurring services' invoices: one per month of their revenue schedule,
 * dated the first day of the month it pays for (billed in advance), due
 * `netDays` later.
 */
export function recurringInvoices(data: QuoteBuilder, netDays: number): Invoice[] {
  const ids = new Set(data.lines.filter(isRecurringService).map((l) => l.id));
  const byStart = new Map<string, number>();
  for (const r of data.schedules ?? []) if (ids.has(r.quote_line_id)) byStart.set(r.period_start, (byStart.get(r.period_start) ?? 0) + r.amount);
  return [...byStart.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, amount]) => ({ date, month: date.slice(0, 7), amount, due: addDays(date, netDays) }));
}
